// api.ts - ملف API الموحد والمصحح
import axios from 'axios';
import { API_URL, getToken, logout, refreshToken, isTokenExpiringSoon } from './config';

// إنشاء instance من axios مع الإعدادات الافتراضية
const api = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15 ثانية
  headers: {
    'Content-Type': 'application/json',
  },
});

// متغير لمنع تكرار تجديد التوكن
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// دالة لإضافة الطلبات المنتظرة
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// دالة لتنفيذ الطلبات المنتظرة بعد تجديد التوكن
const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// إضافة interceptor لإضافة التوكن تلقائياً لكل طلب
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Token added to request:', token.substring(0, 20) + '...');
      
      // التحقق من قرب انتهاء التوكن
      if (isTokenExpiringSoon(5)) {
        console.log('⚠️ Token expiring soon, will refresh on next request');
      }
    } else {
      console.warn('⚠️ No token found for request');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// إضافة interceptor للتعامل مع الأخطاء (خاصة 401)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // لو الخطأ 401 ومش بنحاول نجدد التوكن دلوقتي
    if (error.response?.status === 401 && !originalRequest._retry) {
      
      // لو في طلب تجديد بالفعل، نضيف الطلب ده للقائمة
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // محاولة تجديد التوكن
        const success = await refreshToken();
        
        if (success) {
          const newToken = getToken();
          if (newToken) {
            // تحديث التوكن في الطلب الأصلي
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            
            // تنفيذ كل الطلبات المنتظرة
            onRefreshed(newToken);
            
            // إعادة الطلب الأصلي
            return api(originalRequest);
          }
        }
        
        // لو فشل التجديد، نسجل خروج
        console.error('🔐 Token refresh failed - logging out');
        logout();
        return Promise.reject(error);
        
      } catch (refreshError) {
        console.error('❌ Error during token refresh:', refreshError);
        logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // لو السيرفر مش شغال
    if (error.code === 'ECONNABORTED') {
      console.error('⏰ Request timeout - server might be down');
    }
    
    // لو مفيش اتصال بالنت
    if (error.message === 'Network Error') {
      console.error('🌐 Network error - check your connection');
    }
    
    return Promise.reject(error);
  }
);

// ========== دوال مساعدة للطلبات المتكررة ==========

/**
 * تنفيذ طلب GET
 */
export const get = async <T>(url: string, params?: any): Promise<T> => {
  try {
    const response = await api.get<T>(url, { params });
    return response.data;
  } catch (error) {
    console.error(`❌ GET ${url} failed:`, error);
    throw error;
  }
};

/**
 * تنفيذ طلب POST
 */
export const post = async <T>(url: string, data?: any): Promise<T> => {
  try {
    const response = await api.post<T>(url, data);
    return response.data;
  } catch (error) {
    console.error(`❌ POST ${url} failed:`, error);
    throw error;
  }
};

/**
 * تنفيذ طلب PUT
 */
export const put = async <T>(url: string, data?: any): Promise<T> => {
  try {
    const response = await api.put<T>(url, data);
    return response.data;
  } catch (error) {
    console.error(`❌ PUT ${url} failed:`, error);
    throw error;
  }
};

/**
 * تنفيذ طلب DELETE
 */
export const del = async <T>(url: string): Promise<T> => {
  try {
    const response = await api.delete<T>(url);
    return response.data;
  } catch (error) {
    console.error(`❌ DELETE ${url} failed:`, error);
    throw error;
  }
};

export default api;